import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  QrCodeIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  CameraIcon,
  IdentificationIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  CalendarDaysIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';

const CheckInForm = ({ onSessionUpdate, activeSessions = [] }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [sessionType, setSessionType] = useState('check-in');
  const [scannerActive, setScannerActive] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Données fictives des membres
  const mockMembers = [
    { id: 'M001', memberCode: 'MEM-2023-001', firstName: 'Ahmed', lastName: 'Benaissa', name: 'Ahmed Benaissa', phone: '0551234567', email: 'ahmed.b@email.com', status: 'active', joinDate: '2023-01-15', subscription: { type: 'Mensuel', startDate: '2023-07-01', expiryDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M002', memberCode: 'MEM-2023-002', firstName: 'Fatima', lastName: 'Khelil', name: 'Fatima Khelil', phone: '0661234567', email: 'fatima.k@email.com', status: 'active', joinDate: '2023-01-20', subscription: { type: 'Trimestriel', startDate: '2023-06-01', expiryDate: '2023-09-01', price: 12000 }, photo: null },
    { id: 'M003', memberCode: 'MEM-2023-003', firstName: 'Karim', lastName: 'Messaoud', name: 'Karim Messaoud', phone: '0771234567', email: 'karim.m@email.com', status: 'active', joinDate: '2023-02-05', subscription: { type: 'Annuel', startDate: '2023-02-05', expiryDate: '2024-02-05', price: 40000 }, photo: null },
    { id: 'M004', memberCode: 'MEM-2023-004', firstName: 'Samia', lastName: 'Benarfa', name: 'Samia Benarfa', phone: '0551234568', email: 'samia.b@email.com', status: 'inactive', joinDate: '2023-02-10', subscription: null, photo: null },
    { id: 'M005', memberCode: 'MEM-2023-005', firstName: 'Mohamed', lastName: 'Hakim', name: 'Mohamed Hakim', phone: '0661234568', email: 'mohamed.h@email.com', status: 'active', joinDate: '2023-03-01', subscription: { type: 'Mensuel', startDate: '2023-07-01', expiryDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M006', memberCode: 'MEM-2023-006', firstName: 'Amina', lastName: 'Ferhat', name: 'Amina Ferhat', phone: '0771234568', email: 'amina.f@email.com', status: 'suspended', joinDate: '2023-03-15', subscription: { type: 'Mensuel', startDate: '2023-07-01', expiryDate: '2023-08-01', price: 5000 }, photo: null },
    { id: 'M007', memberCode: 'MEM-2023-007', firstName: 'Youcef', lastName: 'Talbi', name: 'Youcef Talbi', phone: '0551234569', email: 'youcef.t@email.com', status: 'active', joinDate: '2023-04-05', subscription: { type: 'Trimestriel', startDate: '2023-07-05', expiryDate: '2023-10-05', price: 12000 }, photo: null },
    { id: 'M008', memberCode: 'MEM-2023-008', firstName: 'Naima', lastName: 'Kadri', name: 'Naima Kadri', phone: '0661234569', email: 'naima.k@email.com', status: 'active', joinDate: '2023-04-10', subscription: { type: 'Mensuel', startDate: '2023-07-10', expiryDate: '2023-08-10', price: 5000 }, photo: null },
    { id: 'M009', memberCode: 'MEM-2023-009', firstName: 'Hassan', lastName: 'Boudiaf', name: 'Hassan Boudiaf', phone: '0771234569', email: 'hassan.b@email.com', status: 'active', joinDate: '2023-04-20', subscription: { type: 'Mensuel', startDate: '2023-07-20', expiryDate: '2023-08-20', price: 5000 }, photo: null },
    { id: 'M010', memberCode: 'MEM-2023-010', firstName: 'Leila', lastName: 'Mebarki', name: 'Leila Mebarki', phone: '0551234570', email: 'leila.m@email.com', status: 'active', joinDate: '2023-05-01', subscription: { type: 'Annuel', startDate: '2023-05-01', expiryDate: '2024-05-01', price: 40000 }, photo: null }
  ];

  // Fetch members data (avec données fictives)
  const { data: membersData, isLoading: membersLoading } = useQuery('members', async () => {
    try {
      const response = await axios.get('/members');
      return response.data;
    } catch (error) {
      return { members: mockMembers };
    }
  });

  // Check-in/Check-out mutation
  const sessionMutation = useMutation(
    (sessionData) => {
      try {
        // Simuler une requête API réussie
        const newSession = {
          id: Date.now(),
          memberId: sessionData.memberId,
          memberName: mockMembers.find(m => m.id === sessionData.memberId)?.name,
          memberCode: mockMembers.find(m => m.id === sessionData.memberId)?.memberCode,
          checkInTime: new Date().toISOString(),
          checkOutTime: sessionData.type === 'check-out' ? new Date().toISOString() : null,
          type: sessionData.type,
          notes: sessionData.notes,
          memberPhoto: null
        };
        
        return Promise.resolve({ data: { message: 'Session enregistrée avec succès', session: newSession } });
      } catch (error) {
        return Promise.reject(error);
      }
    },
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        queryClient.invalidateQueries('active-sessions');
        queryClient.invalidateQueries('sessions');
        setSelectedMember(null);
        setSessionNotes('');
        setSearchTerm('');
        if (onSessionUpdate) onSessionUpdate();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
      },
    }
  );

  const members = membersData?.members || mockMembers;

  // Filter members based on search term
  const filteredMembers = members.filter(member =>
    member.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.memberCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone?.includes(searchTerm)
  );

  // Check if member has active session
  const hasActiveSession = (memberId) => {
    return activeSessions.some(session => 
      session.memberId === memberId && !session.checkOutTime
    );
  };

  // Get active session for member
  const getActiveSession = (memberId) => {
    return activeSessions.find(session => 
      session.memberId === memberId && !session.checkOutTime
    );
  };

  // Handle member selection
  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    setSearchTerm('');
    setShowMemberDetails(true);
    
    // Auto-detect session type based on current status
    if (hasActiveSession(member.id)) {
      setSessionType('check-out');
    } else {
      setSessionType('check-in');
    }
  };

  // Handle session submission
  const handleSessionSubmit = () => {
    if (!selectedMember) {
      toast.error('Veuillez sélectionner un membre');
      return;
    }

    const sessionData = {
      memberId: selectedMember.id,
      type: sessionType,
      notes: sessionNotes,
    };

    sessionMutation.mutate(sessionData);
  };

  // Start barcode/QR scanner
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      videoRef.current.srcObject = stream;
      setScannerActive(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Impossible d\'accéder à la caméra');
    }
  };

  // Stop scanner
  const stopScanner = () => {
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setScannerActive(false);
  };

  // Format duration
  const formatDuration = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const duration = Math.floor((now - start) / 1000 / 60); // minutes
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  // Get member status
  const getMemberStatus = (member) => {
    if (!member.subscription) return { status: 'inactive', color: 'red', text: 'Aucun abonnement' };
    
    const expiry = new Date(member.subscription.expiryDate);
    const now = new Date();
    
    if (expiry < now) {
      return { status: 'expired', color: 'red', text: 'Abonnement expiré' };
    }
    
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry <= 7) {
      return { status: 'expiring', color: 'orange', text: `Expire dans ${daysUntilExpiry} jours` };
    }
    
    return { status: 'active', color: 'green', text: 'Abonnement actif' };
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Search Section */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/40" />
            <input
              type="text"
              placeholder="Rechercher un membre (nom, code, email, téléphone...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Scanner Button */}
          <button
            onClick={scannerActive ? stopScanner : startScanner}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-colors ${
              scannerActive 
                ? 'bg-red-500 text-white hover:bg-red-600' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {scannerActive ? (
              <>
                <XMarkIcon className="h-5 w-5" />
                <span>Arrêter</span>
              </>
            ) : (
              <>
                <QrCodeIcon className="h-5 w-5" />
                <span>Scanner</span>
              </>
            )}
          </button>
        </div>

        {/* Scanner View */}
        {scannerActive && (
          <div className="mt-4 relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full max-w-md mx-auto rounded-xl"
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-48 h-48 border-2 border-blue-500 rounded-lg opacity-50"></div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchTerm && filteredMembers.length > 0 && (
          <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
            {filteredMembers.slice(0, 10).map((member) => {
              const status = getMemberStatus(member);
              const isActive = hasActiveSession(member.id);
              
              return (
                <div
                  key={member.id}
                  onClick={() => handleMemberSelect(member)}
                  className="flex items-center justify-between p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      {member.photo ? (
                        <img src={member.photo} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-white font-semibold text-sm">
                          {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-white font-medium">{member.firstName} {member.lastName}</p>
                      <div className="flex items-center space-x-2">
                        <span className="text-white/60 text-xs">{member.memberCode}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full bg-${status.color}-500/20 text-${status.color}-400`}>
                          {status.text}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {isActive && (
                    <div className="flex items-center space-x-1 bg-green-500/20 text-green-400 px-2 py-1 rounded-lg">
                      <ClockIcon className="h-3 w-3" />
                      <span className="text-xs">
                        {formatDuration(getActiveSession(member.id).checkInTime)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {searchTerm && filteredMembers.length === 0 && (
          <div className="mt-4 p-4 text-center text-white/60 bg-white/5 rounded-xl">
            Aucun membre trouvé. Veuillez essayer avec un autre terme de recherche.
          </div>
        )}
      </div>

      {/* Member Details & Check-in Form */}
      {selectedMember && showMemberDetails && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          {/* Member Info */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                {selectedMember.photo ? (
                  <img src={selectedMember.photo} alt={selectedMember.name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-xl">
                    {selectedMember.firstName?.charAt(0)}{selectedMember.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {selectedMember.firstName} {selectedMember.lastName}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-white/60 text-sm">{selectedMember.memberCode}</span>
                  
                  {hasActiveSession(selectedMember.id) ? (
                    <div className="flex items-center space-x-1 bg-green-500/20 text-green-400 px-2 py-0.5 rounded-lg">
                      <ClockIcon className="h-3 w-3" />
                      <span className="text-xs">
                        En salle depuis {formatDuration(getActiveSession(selectedMember.id).checkInTime)}
                      </span>
                    </div>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${getMemberStatus(selectedMember).color}-500/20 text-${getMemberStatus(selectedMember).color}-400`}>
                      {getMemberStatus(selectedMember).text}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowMemberDetails(false)}
              className="text-white/60 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Quick Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-xl">
              <PhoneIcon className="h-5 w-5 text-white/50" />
              <div>
                <p className="text-white/60 text-xs">Téléphone</p>
                <p className="text-white">{selectedMember.phone}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-xl">
              <UserCircleIcon className="h-5 w-5 text-white/50" />
              <div>
                <p className="text-white/60 text-xs">Abonnement</p>
                <p className="text-white">{selectedMember.subscription?.type || 'Aucun'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-xl">
              <CalendarDaysIcon className="h-5 w-5 text-white/50" />
              <div>
                <p className="text-white/60 text-xs">Expiration</p>
                <p className="text-white">
                  {selectedMember.subscription ? 
                    new Date(selectedMember.subscription.expiryDate).toLocaleDateString('fr-FR') : 
                    'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Session Type Selection */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setSessionType('check-in')}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl transition-colors ${
                  sessionType === 'check-in' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                } ${hasActiveSession(selectedMember.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={hasActiveSession(selectedMember.id)}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span>Check-in</span>
              </button>
              
              <button
                onClick={() => setSessionType('check-out')}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl transition-colors ${
                  sessionType === 'check-out' 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                } ${!hasActiveSession(selectedMember.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!hasActiveSession(selectedMember.id)}
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                <span>Check-out</span>
              </button>
            </div>
            
            {!selectedMember.subscription && (
              <div className="mt-3 p-3 bg-red-500/20 text-red-400 rounded-xl flex items-start space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Attention : Aucun abonnement actif</p>
                  <p className="text-sm">Ce membre n'a pas d'abonnement actif. Voulez-vous quand même procéder ?</p>
                </div>
              </div>
            )}
            
            {selectedMember.subscription && new Date(selectedMember.subscription.expiryDate) < new Date() && (
              <div className="mt-3 p-3 bg-orange-500/20 text-orange-400 rounded-xl flex items-start space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Attention : Abonnement expiré</p>
                  <p className="text-sm">
                    L'abonnement de ce membre a expiré le {new Date(selectedMember.subscription.expiryDate).toLocaleDateString('fr-FR')}. 
                    Voulez-vous quand même procéder ?
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label htmlFor="notes" className="block text-white/70 mb-2">
              Notes (facultatif)
            </label>
            <textarea
              id="notes"
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="Ajouter des notes sur cette séance..."
              className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSessionSubmit}
              disabled={sessionMutation.isLoading}
              className={`flex items-center space-x-2 py-3 px-6 rounded-xl font-medium ${
                sessionType === 'check-in' 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {sessionMutation.isLoading ? (
                <div className="animate-spin h-5 w-5 border-2 border-t-transparent border-white rounded-full mr-2"></div>
              ) : sessionType === 'check-in' ? (
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              ) : (
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              )}
              <span>
                {sessionType === 'check-in' ? 'Enregistrer l\'entrée' : 'Enregistrer la sortie'}
              </span>
            </button>
          </div>
        </motion.div>
      )}

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">
            Séances en cours ({activeSessions.length})
          </h3>
          
          <div className="space-y-3">
            {activeSessions.map((session) => (
              <div 
                key={session.id} 
                className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    {session.memberPhoto ? (
                      <img src={session.memberPhoto} alt={session.memberName} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <span className="text-white font-semibold text-sm">
                        {session.memberName?.charAt(0)}
                      </span>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-white font-medium">{session.memberName}</p>
                    <p className="text-white/60 text-sm">{session.memberCode}</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-end">
                  <div className="flex items-center space-x-1 bg-green-500/20 text-green-400 px-2 py-1 rounded-lg">
                    <ClockIcon className="h-3 w-3" />
                    <span className="text-xs">
                      {formatDuration(session.checkInTime)}
                    </span>
                  </div>
                  <p className="text-white/60 text-xs mt-1">
                    {new Date(session.checkInTime).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CheckInForm; 